import { CheckCircle, Info, KeyboardDoubleArrowLeft, KeyboardDoubleArrowRight } from "@mui/icons-material";
import { Box, Tooltip, Typography } from "@mui/material";
import { HealthApplicationQueryParams } from "../_common/enum/healthApplicationQueryParams.enum";
import { QuoteTypeEnum } from "../_common/enum/quoteType.enum";
import { IQuoteProviderComparisonResults } from "../_common/interface/quoteProviderComparison/iQuoteProviderComparisonResults.interface";
import { getMoneyFormat } from "./format.helper";

export function getMoneyKFormat(amount: number) {
    let formattedAmount: string;

    if (amount >= 1_000_000) {
        formattedAmount = (amount / 1_000_000).toFixed(1) + 'M';
    } else if (amount >= 1_000) {
        formattedAmount = (amount / 1_000).toFixed(1) + 'k';
    } else {
        formattedAmount = amount.toString();
    }

    return formattedAmount;
}

export function formatQuoteDescription(
    text: string,
    isRegularQuote: boolean,
    employmentType: string | null
) {
    const BREAK = "||BREAK||";

    // Normalize breaks and clean up whitespace
    const rawLines = text
        .replace(/(\n|<br\s*\/?>)/g, BREAK)
        .split(BREAK)
        .map(l => l.trim())
        .filter(Boolean);

    // Group by person, a new block starts when a line contains name
    type PersonBlock = { header: string; lines: string[] };
    const people: PersonBlock[] = [];
    let current: PersonBlock | null = null;

    for (const line of rawLines) {
        if (line.includes(":")) {
            current = { header: line, lines: [line] };
            people.push(current);
        } else {
            if (!current) {
                // Single person
                current = { header: "", lines: [line] };
                people.push(current);
            } else {
                current.lines.push(line);
            }
        }
    }

    // IP Tooltip info
    const TooltipInfo = !!isRegularQuote && !!employmentType ? (
        <Tooltip
            enterTouchDelay={0}
            leaveTouchDelay={3000}
            title={
                employmentType === "Employee"
                    ? `Maximum amount of income protection cover is equal to 75% of earnings before disability benefit of  \u20AC13,208`
                    : "Maximum amount of income protection cover: 75% applied to quote"
            }
        >
            <Info color="info" />
        </Tooltip>
    ) : null;

    const renderRegularLine = (key: React.Key, line: string) => (
        <Box
            key={key}
            display="flex"
            justifyContent={isRegularQuote ? "flex-start" : "center"}
            alignItems="flex-start"     
            mt={1}
        >
            {isRegularQuote && (
                <CheckCircle
                    sx={(theme) => ({
                        mr: 0.5,
                        mt: { xs: "2px", md: "4px" },  
                        color: theme.palette.pulse.main,
                        fontSize: { xs: 20, md: 24 },
                    })}
                />
            )}

            <Typography variant="h6" fontWeight="bold">
                {!isRegularQuote && (
                    <Box component="span" mr={0.5}>
                        &#8226;
                    </Box>
                )}
                {line} {TooltipInfo}
            </Typography>
        </Box>

    );

    const renderPulsingLine = (key: React.Key, line: string) => (
        <Box
            key={key}
            className="pulsing-text"
            sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "pulse.main"
            }}
        >
            <KeyboardDoubleArrowRight sx={{ fontSize: { xs: "small", md: "large" } }} />
            <Typography variant="h6">{line}</Typography>
            <KeyboardDoubleArrowLeft sx={{ fontSize: { xs: "small", md: "large" } }} />
        </Box>
    );

 
    return people.flatMap((person, pIdx) => {
        const hasSecondLine = person.lines.length > 1;

        return person.lines.flatMap((line, lIdx) => {
            const key = `${pIdx}-${lIdx}`;
            const isSecondForThisPerson = lIdx === 1 && hasSecondLine;
            const shouldPulse = !isRegularQuote && isSecondForThisPerson;

            const content = shouldPulse ? renderPulsingLine(key, line) : renderRegularLine(key, line);

            const plusBetween =
                !isRegularQuote && hasSecondLine && lIdx === 0 ? (
                    <div key={`plus-${pIdx}`} style={{ display: "block" }}>
                        <Typography textAlign="center" variant="h6">
                            +
                        </Typography>
                    </div>
                ) : null;

            return plusBetween ? [content, plusBetween] : [content];
        });
    });
}


export function formatQuotePrice(value: number, addPlus: boolean) {
    const [integerPricePart, fractionalPricePart] = getMoneyFormat(value).split(".");

    const formattedValue = (
        <>
            <span>
                <span>{addPlus ? "+" + integerPricePart : integerPricePart}</span>
                {fractionalPricePart && (
                    <span>
                        .{fractionalPricePart}
                    </span>
                )}
            </span>

        </>
    );
    return formattedValue
}

export function getAgeAndMonths(dateOfBirth: string) {
    dateOfBirth = dateOfBirth.replace(/\s\d{2}:\d{2}:\d{2}$/, "");

    const [day, month, year] = dateOfBirth.split("/").map(Number);
    const birthDate = new Date(year, month - 1, day);
    const today = new Date();

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();

    if (months < 0) {
        years--;
        months += 12;
    }

    return `${years} Years, ${months} Months`;
}

export function isOlderThan(day: string, month: string, year: string, age: number) {
    const birthDate = new Date(Number(year), Number(month) - 1, Number(day)); // month is 0-based
    const today = new Date();

    const cutoffDate = new Date(
        today.getFullYear() - age,
        today.getMonth(),
        today.getDate()
    );

    return birthDate <= cutoffDate;
}

export function isGreenMortgage(mortgageName: string) {
    return /\bgreen\b/i.test(mortgageName);
}

//export function buildHealthApplicationUrl(data: any, providerName: string) {
//    const providerNormalized = normalizeProvider(providerName);

//    const isSmoker = data.smokingStatus === "Smoker" ? "Smoker" : "Non-Smoker"
//    const isJointLifeSmoker = data.smokingStatusJointLife
//        ? data.smokingStatusJointLife === "Smoker" ? "Smoker" : "Non-Smoker"
//        : null;

//    const peopleCovered = data.isJointLife ? "two" : "one"

//    const baseUrl = data.quoteType === QuoteTypeEnum.IncomeProtection
//        ? "https://lowquotes.ie/preliminary-health-application2/"
//        : "https://lowquotes.ie/preliminary-health-application1/";

//    const queryParams = new URLSearchParams({
//        [HealthApplicationQueryParams.Provider]: providerNormalized,
//        [HealthApplicationQueryParams.QuoteRef]: data.quoteRef,
//        [HealthApplicationQueryParams.PeopleOnPolicy]: peopleCovered.toString(),
//        [HealthApplicationQueryParams.FirstName]: data.firstName,
//        [HealthApplicationQueryParams.LastName]: data.lastName,
//        [HealthApplicationQueryParams.DOB]: data.dateOfBirth ?? "",
//        [HealthApplicationQueryParams.Occupation]: data.occupation ?? "",
//        [HealthApplicationQueryParams.Phone]: data.phoneNumber ?? "",
//        [HealthApplicationQueryParams.Email]: data.emailAddress ?? "",
//        [HealthApplicationQueryParams.Smoker]: isSmoker,
//        [HealthApplicationQueryParams.JointFirstName]: data.firstNameJointLife ?? "",
//        [HealthApplicationQueryParams.JointLastName]: data.lastNameJointLife ?? "",
//        [HealthApplicationQueryParams.JointDOB]: data.dateOfBirthJointLife ?? ""
//    });

//    return `${baseUrl}?${queryParams.toString()}`;
//}


//function normalizeProvider(providerName: string) {
//    console.log("provider name", providerName);
//    switch (providerName) {
//        case "Royal London":
//            return "rlondon";
//        case "Zurich Life":
//            return "zurich";
//        default:
//            return providerName.replace(/ /g, "").toLowerCase();
//    }
//}